import type { PlayerRef } from '@remotion/player';
import React, { useCallback, useEffect, useState } from 'react';
import { ToolButton } from '@crabs-ai/ds-react';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit,
} from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { isEventTargetInputElement } from '../utils/is-event-target-input-element';

export const FullscreenButton: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ playerRef }) => {
  const t = useTranslations();
  const [supportsFullscreen, setSupportsFullscreen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const { current } = playerRef;
    if (!current) return;

    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement !== null);
    };

    current.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      current.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [playerRef]);

  useEffect(() => {
    setSupportsFullscreen(
      (typeof document !== 'undefined' &&
        (document.fullscreenEnabled ||
          // @ts-expect-error Types not defined
          document.webkitFullscreenEnabled)) ??
        false,
    );
  }, []);

  const toggleFullscreen = useCallback(() => {
    const { current } = playerRef;
    if (!current) return;

    if (isFullscreen) {
      current.exitFullscreen();
    } else {
      current.requestFullscreen();
    }
  }, [isFullscreen, playerRef]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isEventTargetInputElement(e)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.code === 'KeyF') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen]);

  if (!supportsFullscreen) {
    return null;
  }

  return (
    <ToolButton
      size="sm"
      state="default"
      onClick={toggleFullscreen}
      isShowTooltip
      tooltipNode={
        isFullscreen
          ? t('Editor.timeline-controls.exit-fullscreen')
          : t('Editor.timeline-controls.fullscreen')
      }
      tooltipSubNode="[F]"
      tooltipPosition="top"
    >
      {isFullscreen ? <FullscreenExit /> : <FullscreenIcon />}
    </ToolButton>
  );
};
